import { deepEqual, ia, iaRa, safeParseJSON } from 'lib/helpers/utility';

import {
  arrayOptions,
  multipleArrayOptions,
  numberOptions,
  numberRangeOptions,
  presenceOptions,
  signatureStatusOptions,
  singleArrayOptions,
  textOptions
} from './formulas';

const NO_FILTERS = 'none';
const SEARCH_FIELDS_SLUG = 'in';
const DEFAULT_FORMULA = 'is';
const KNOWN_FORMULAS = new Set([
  ...multipleArrayOptions,
  ...singleArrayOptions,
  ...arrayOptions,
  ...textOptions,
  ...numberOptions,
  ...numberRangeOptions,
  ...signatureStatusOptions,
  ...presenceOptions
]);

const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);
const isPrimitive = (value) =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
const isSearchFilter = (config) => config?.type === 'search';
const isObjectValued = (formula, config) =>
  config?.type === 'date-range' || numberRangeOptions.includes(formula);

export const getFiltersParamPrefix = (paramPrefix) => paramPrefix || 'filters';

const isFiltersParam = (key, prefix) => key === prefix || key.startsWith(`${prefix}.`);

const toFormulaSlug = (formula) => formula.replaceAll(' ', '_');
const fromFormulaSlug = (slug) => slug.replaceAll('_', ' ');

const toUrlValues = (values) =>
  Array.isArray(values)
    ? values.map((item) => {
        const value = isPlainObject(item) && 'value' in item ? item.value : item;
        return isPrimitive(value) ? String(value) : value;
      })
    : values;

const toUrlEntry = (entry, defaultEntry) =>
  Object.fromEntries(
    Object.entries(entry)
      .filter(
        ([prop, value]) => typeof value !== 'function' && !deepEqual(value, defaultEntry?.[prop])
      )
      .map(([prop, value]) => [prop, prop === 'values' ? toUrlValues(value) : value])
  );

const isFilledValue = (value) => value !== null && value !== undefined && value !== '';

const hasFilterValue = (values) => {
  if (Array.isArray(values)) return values.length > 0;
  if (isPlainObject(values)) return Object.values(values).some(isFilledValue);
  return isFilledValue(values);
};

const getSearchFieldNames = (config) => iaRa(config?.fields).map((item) => item.field);

const toUrlSearch = (entries, config) => {
  const { searchTerm, fields } = entries[0]?.values || {};
  if (typeof searchTerm !== 'string' || !searchTerm.trim()) return null;

  const searchFields = getSearchFieldNames(config);
  if (!Array.isArray(fields) || searchFields.every((field) => fields.includes(field))) {
    return { searchTerm };
  }
  return { searchTerm, fields: searchFields.filter((field) => fields.includes(field)) };
};

const toUrlFilterEntries = (entries, config) =>
  entries
    .filter((entry) => isPlainObject(entry) && !isSearchFilter(entry))
    .filter((entry) => config?.default || hasFilterValue(entry.values))
    .map((entry) => toUrlEntry(entry, config));

const toUrlFilters = (filters, defaultFilters) =>
  Object.fromEntries(
    Object.entries(filters || {})
      .filter(([, entries]) => Array.isArray(entries))
      .map(([key, entries]) => [
        key,
        isSearchFilter(defaultFilters?.[key])
          ? toUrlSearch(entries, defaultFilters[key])
          : toUrlFilterEntries(entries, defaultFilters?.[key])
      ])
      .filter(([, value]) => ia(value) || isPlainObject(value))
  );

const toParamValues = (values) => {
  if (Array.isArray(values)) {
    const items = values.filter(isPrimitive).map(String);
    return ia(items) ? items : [''];
  }
  if (isPlainObject(values)) return [JSON.stringify(values)];
  if (values === null || values === undefined) return [''];
  return [String(values)];
};

const toEntryParams = (entry, config, fieldKey) => {
  if (!('formula' in entry) && !('values' in entry)) return [[fieldKey, '']];

  const formula = entry.formula ?? config?.formula ?? DEFAULT_FORMULA;
  const values = 'values' in entry ? entry.values : toUrlValues(config?.values);
  const key = `${fieldKey}.${toFormulaSlug(formula)}`;

  return toParamValues(values).map((value) => [key, value]);
};

const toSearchParams = ({ searchTerm, fields }, fieldKey) => [
  [fieldKey, searchTerm],
  ...(fields
    ? toParamValues(fields).map((field) => [`${fieldKey}.${SEARCH_FIELDS_SLUG}`, field])
    : [])
];

export const serializeUrlFilters = (filters, defaultFilters, paramPrefix) => {
  const prefix = getFiltersParamPrefix(paramPrefix);
  const params = Object.entries(toUrlFilters(filters, defaultFilters)).flatMap(([key, value]) => {
    const fieldKey = `${prefix}.${key}`;
    return Array.isArray(value)
      ? value.flatMap((entry) => toEntryParams(entry, defaultFilters?.[key], fieldKey))
      : toSearchParams(value, fieldKey);
  });

  return new URLSearchParams(ia(params) ? params : [[prefix, NO_FILTERS]]).toString();
};

export const isSameUrlFilters = (filters, otherFilters, defaultFilters) =>
  deepEqual(toUrlFilters(filters, defaultFilters), toUrlFilters(otherFilters, defaultFilters));

export const getUrlFiltersQuery = (searchParams, paramPrefix) => {
  const prefix = getFiltersParamPrefix(paramPrefix);
  const params = [...searchParams.entries()].filter(([key]) => isFiltersParam(key, prefix));

  return ia(params) ? new URLSearchParams(params).toString() : null;
};

export const replaceUrlFilters = (searchParams, paramPrefix, filtersQuery) => {
  const prefix = getFiltersParamPrefix(paramPrefix);
  const newParams = new URLSearchParams(searchParams);

  [...new Set(newParams.keys())]
    .filter((key) => isFiltersParam(key, prefix))
    .forEach((key) => newParams.delete(key));
  new URLSearchParams(filtersQuery || '').forEach((value, key) => newParams.append(key, value));

  return newParams;
};

const isAllowedFormula = (formula, config) => {
  if (!KNOWN_FORMULAS.has(formula)) return false;
  if (!ia(config.allowed_operators)) return true;
  return config.allowed_operators.includes(formula) || formula === 'is any of';
};

const getEntryType = (formula) => {
  if (textOptions.includes(formula) || presenceOptions.includes(formula)) return 'text';
  if (numberOptions.includes(formula)) return 'number';
  if (numberRangeOptions.includes(formula)) return 'number-range';
  return undefined;
};

const toListValue = (value) => {
  if (value === 'true' || value === 'false') return value === 'true';
  return /^(0|-?[1-9]\d*)$/.test(value) && Number.isSafeInteger(Number(value))
    ? Number(value)
    : value;
};

const toNumberValue = (value) =>
  value !== '' && Number.isFinite(Number(value)) ? Number(value) : value;

const isListFormula = (formula, config) =>
  !isObjectValued(formula, config) &&
  !textOptions.includes(formula) &&
  !numberOptions.includes(formula) &&
  !presenceOptions.includes(formula);

const toEntry = (formula, value, config) => {
  const type = getEntryType(formula);
  const entry = { formula, ...(type && { type }) };

  if (presenceOptions.includes(formula)) return { ...entry, values: true };
  if (value === '') return entry;
  if (isObjectValued(formula, config)) {
    const values = safeParseJSON(value);
    return isPlainObject(values) ? { ...entry, values } : null;
  }
  if (numberOptions.includes(formula)) return { ...entry, values: toNumberValue(value) };
  return { ...entry, values: value };
};

const toListEntry = (formula, value, config) => {
  if (value !== '') return { formula, values: [{ value: toListValue(value) }] };
  return ia(config.values) ? { formula, values: [] } : { formula };
};

const addListValue = (entries, formula, value, config) => {
  const listEntry = entries.find((entry) => entry.formula === formula);
  if (!listEntry) return [...entries, toListEntry(formula, value, config)];
  if (value === '') return entries;

  const values = [...(listEntry.values || []), { value: toListValue(value) }];
  return entries.map((entry) => (entry === listEntry ? { ...entry, values } : entry));
};

const addParamEntry = (entries, slug, value, config) => {
  if (!slug) return [...entries, {}];

  const formula = fromFormulaSlug(slug);
  if (!isAllowedFormula(formula, config)) return entries;
  if (isListFormula(formula, config)) return addListValue(entries, formula, value, config);

  const entry = toEntry(formula, value, config);
  return entry ? [...entries, entry] : entries;
};

const addSearchParam = (values = {}, slug, value) => {
  if (!slug) return { ...values, searchTerm: value };
  if (slug !== SEARCH_FIELDS_SLUG) return values;
  return { ...values, fields: [...(values.fields || []), ...(value ? [value] : [])] };
};

const toSearchEntry = ({ searchTerm, fields }, config) => {
  if (!searchTerm?.trim()) return null;

  const searchFields = getSearchFieldNames(config);
  return {
    type: 'search',
    values: {
      searchTerm,
      fields: fields ? searchFields.filter((field) => fields.includes(field)) : searchFields
    }
  };
};

export const parseUrlFilters = (filtersQuery, defaultFilters, paramPrefix) => {
  if (!filtersQuery) return null;

  const prefix = getFiltersParamPrefix(paramPrefix);
  const filters = {};
  const searches = {};

  new URLSearchParams(filtersQuery).forEach((value, key) => {
    if (!key.startsWith(`${prefix}.`)) return;

    const [field, slug, ...rest] = key.slice(prefix.length + 1).split('.');
    const config = defaultFilters?.[field];
    if (!isPlainObject(config) || rest.length) return;

    if (isSearchFilter(config)) {
      searches[field] = addSearchParam(searches[field], slug, value);
      return;
    }
    filters[field] = addParamEntry(filters[field] || [], slug, value, config);
  });

  Object.entries(searches).forEach(([field, values]) => {
    const entry = toSearchEntry(values, defaultFilters[field]);
    if (entry) filters[field] = [entry];
  });

  return { filters: JSON.stringify(filters) };
};

const FILTERS_PARAM_PATTERN = /^[^.]+\.[^.]+/;
const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+.-]*:/i;

export const stripFiltersParams = (url) => {
  if (!url) return url;

  try {
    const parsedUrl = new URL(url, window.location.origin);
    const filterKeys = [...new Set(parsedUrl.searchParams.keys())].filter((key) =>
      FILTERS_PARAM_PATTERN.test(key)
    );
    if (!filterKeys.length) return url;

    filterKeys.forEach((key) => parsedUrl.searchParams.delete(key));
    return ABSOLUTE_URL_PATTERN.test(url)
      ? parsedUrl.toString()
      : `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch {
    return url;
  }
};
